import { renderBlockIcon, renderItemIcon } from './Hotbar.js';
import { MAX_STACK_SIZE } from '../constants.js';

const SLOT_SIZE = 44;
const GAP = 2;
const COLS = 9;

/**
 * FurnaceScreen — Minecraft-style furnace UI.
 * Shows input slot, fuel slot, progress arrow, output slot,
 * plus 27 player storage + 9 hotbar.
 * Uses inventory.getUnifiedSlot/setUnifiedSlot for player slots.
 */
export class FurnaceScreen {
  constructor(inventory) {
    this.inventory = inventory;
    this.isOpen = false;
    this.furnaceData = null; // { input, fuel, output, progress, fuelLeft }
    this.heldItem = null;
    this._onClose = null;

    this._build();
  }

  _build() {
    this.overlay = document.createElement('div');
    this.overlay.id = 'furnace-overlay';
    this.overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.55); z-index: 500;
      display: none; justify-content: center; align-items: center;
      cursor: default;
    `;

    this.panel = document.createElement('div');
    this.panel.style.cssText = `
      background: #C6C6C6;
      border: 4px solid #2A2A2A;
      border-top-color: #FFFFFF; border-left-color: #FFFFFF;
      padding: 8px;
      font-family: 'Courier New', monospace; color: #404040;
      user-select: none; image-rendering: pixelated;
      box-shadow: 4px 4px 0 rgba(0,0,0,0.3), inset 0 0 0 2px #8B8B8B;
    `;

    // Title
    this.titleEl = document.createElement('div');
    this.titleEl.textContent = 'Horno';
    this.titleEl.style.cssText = 'font-size: 14px; margin-bottom: 6px; color: #404040;';
    this.panel.appendChild(this.titleEl);

    // Furnace area: input, fuel, arrow, flame, output
    this.furnaceArea = document.createElement('div');
    this.furnaceArea.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      margin-bottom: 10px;
      padding: 10px 20px;
      min-width: ${COLS * (SLOT_SIZE + GAP)}px;
    `;

    // Left column: input slot (top) + fuel slot (bottom)
    const leftCol = document.createElement('div');
    leftCol.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
    `;

    // Input slot
    this.inputSlot = this._createSlot();
    this.inputSlot.addEventListener('click', () => this._onFurnaceSlotClick('input'));
    this.inputSlot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onFurnaceSlotRightClick('input'); });
    leftCol.appendChild(this.inputSlot);

    // Fuel indicator (flame) between input and fuel
    this.flameContainer = document.createElement('div');
    this.flameContainer.style.cssText = `
      width: 18px;
      height: 18px;
      position: relative;
      overflow: hidden;
    `;

    // Flame background (gray)
    const flameBg = document.createElement('div');
    flameBg.style.cssText = `
      position: absolute;
      top: 0; left: 0; width: 100%; height: 100%;
      font-size: 18px; line-height: 18px; text-align: center;
      color: #666;
    `;
    flameBg.textContent = '\u{1F525}';
    this.flameContainer.appendChild(flameBg);

    // Flame fill (red/orange, clips from bottom)
    this.flameFill = document.createElement('div');
    this.flameFill.style.cssText = `
      position: absolute;
      bottom: 0; left: 0; width: 100%; height: 0%;
      overflow: hidden;
      font-size: 18px; line-height: 18px; text-align: center;
      color: #FF4400;
    `;
    // Inner element positioned at bottom of container
    this.flameFillInner = document.createElement('div');
    this.flameFillInner.style.cssText = `
      position: absolute;
      bottom: 0; left: 0; width: 100%;
      font-size: 18px; line-height: 18px; text-align: center;
    `;
    this.flameFillInner.textContent = '\u{1F525}';
    this.flameFill.appendChild(this.flameFillInner);
    this.flameContainer.appendChild(this.flameFill);
    leftCol.appendChild(this.flameContainer);

    // Fuel slot
    this.fuelSlot = this._createSlot();
    this.fuelSlot.addEventListener('click', () => this._onFurnaceSlotClick('fuel'));
    this.fuelSlot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onFurnaceSlotRightClick('fuel'); });
    leftCol.appendChild(this.fuelSlot);

    this.furnaceArea.appendChild(leftCol);

    // Center: progress arrow
    const arrowContainer = document.createElement('div');
    arrowContainer.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    this.arrowEl = document.createElement('div');
    this.arrowEl.style.cssText = `
      width: 44px;
      height: 24px;
      background: #8B8B8B;
      position: relative;
      border: 2px solid #6A6A6A;
      overflow: hidden;
    `;

    // Arrow fill (progress)
    this.arrowFill = document.createElement('div');
    this.arrowFill.style.cssText = `
      position: absolute;
      top: 0; left: 0;
      width: 0%;
      height: 100%;
      background: #FFFFFF;
      transition: width 0.1s linear;
    `;
    this.arrowEl.appendChild(this.arrowFill);

    // Arrow symbol overlay
    const arrowSymbol = document.createElement('div');
    arrowSymbol.style.cssText = `
      position: absolute;
      top: 0; left: 0; width: 100%; height: 100%;
      display: flex; align-items: center; justify-content: center;
      font-size: 16px; color: #404040;
      pointer-events: none;
      z-index: 1;
    `;
    arrowSymbol.textContent = '\u{27A1}';
    this.arrowEl.appendChild(arrowSymbol);

    arrowContainer.appendChild(this.arrowEl);
    this.furnaceArea.appendChild(arrowContainer);

    // Right: output slot
    this.outputSlot = this._createSlot();
    this.outputSlot.style.width = (SLOT_SIZE + 8) + 'px';
    this.outputSlot.style.height = (SLOT_SIZE + 8) + 'px';
    this.outputSlot.addEventListener('click', () => this._onOutputSlotClick());
    this.outputSlot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onOutputSlotRightClick(); });
    this.furnaceArea.appendChild(this.outputSlot);

    this.panel.appendChild(this.furnaceArea);

    // Separator
    const sep = document.createElement('div');
    sep.style.cssText = 'height: 2px; background: #8B8B8B; margin: 6px 0;';
    this.panel.appendChild(sep);

    // Player inventory label
    const invLabel = document.createElement('div');
    invLabel.textContent = 'Inventario';
    invLabel.style.cssText = 'font-size: 12px; margin-bottom: 4px; color: #606060;';
    this.panel.appendChild(invLabel);

    // Player storage (27 slots: unified indices 9-35)
    this.playerGrid = document.createElement('div');
    this.playerGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${COLS}, ${SLOT_SIZE}px);
      gap: ${GAP}px;
      margin-bottom: 6px;
    `;
    this.playerSlots = [];
    for (let i = 0; i < 27; i++) {
      const slot = this._createSlot();
      slot.addEventListener('click', () => this._onPlayerSlotClick(i + 9));
      slot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onPlayerSlotRightClick(i + 9); });
      this.playerGrid.appendChild(slot);
      this.playerSlots.push(slot);
    }
    this.panel.appendChild(this.playerGrid);

    // Player hotbar (9 slots: unified indices 0-8)
    const hotbarSep = document.createElement('div');
    hotbarSep.style.cssText = 'height: 2px; background: #8B8B8B; margin: 4px 0;';
    this.panel.appendChild(hotbarSep);

    this.hotbarGrid = document.createElement('div');
    this.hotbarGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${COLS}, ${SLOT_SIZE}px);
      gap: ${GAP}px;
    `;
    this.hotbarSlots = [];
    for (let i = 0; i < 9; i++) {
      const slot = this._createSlot();
      slot.addEventListener('click', () => this._onPlayerSlotClick(i));
      slot.addEventListener('contextmenu', (e) => { e.preventDefault(); this._onPlayerSlotRightClick(i); });
      this.hotbarGrid.appendChild(slot);
      this.hotbarSlots.push(slot);
    }
    this.panel.appendChild(this.hotbarGrid);

    // Held item cursor
    this.cursorEl = document.createElement('div');
    this.cursorEl.style.cssText = `
      position: fixed; pointer-events: none; z-index: 600;
      width: ${SLOT_SIZE}px; height: ${SLOT_SIZE}px;
      display: none;
    `;
    document.body.appendChild(this.cursorEl);

    this.overlay.appendChild(this.panel);
    document.body.appendChild(this.overlay);

    // Track mouse for held item
    this._onMouseMove = (e) => {
      if (!this.isOpen) return;
      this.cursorEl.style.left = (e.clientX - SLOT_SIZE / 2) + 'px';
      this.cursorEl.style.top = (e.clientY - SLOT_SIZE / 2) + 'px';
    };
    // mousemove listener added in open(), removed in close()
  }

  _createSlot() {
    const slot = document.createElement('div');
    slot.style.cssText = `
      width: ${SLOT_SIZE}px; height: ${SLOT_SIZE}px;
      background: #8B8B8B;
      border: 2px solid #373737;
      border-top-color: #FFFFFF; border-left-color: #FFFFFF;
      position: relative; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
    `;
    return slot;
  }

  _itemsMatch(a, b) {
    if (!a || !b) return false;
    if (a.blockId !== undefined && b.blockId !== undefined) return a.blockId === b.blockId;
    if (a.itemId !== undefined && b.itemId !== undefined) return a.itemId === b.itemId;
    return false;
  }

  _isEmpty(item) {
    return !item || (!item.count) || (item.blockId === 0 && item.itemId === undefined);
  }

  open(furnaceData, onClose) {
    this.furnaceData = furnaceData;
    this._onClose = onClose;
    this.heldItem = null;
    this.cursorEl.style.display = 'none';
    this.isOpen = true;
    this.overlay.style.display = 'flex';
    document.addEventListener('mousemove', this._onMouseMove);
    this._render();
  }

  close() {
    if (this.heldItem) {
      // Return held item to player inventory
      if (this.heldItem.blockId !== undefined) {
        this.inventory.addBlock(this.heldItem.blockId);
      } else if (this.heldItem.itemId !== undefined) {
        this.inventory.addItem(this.heldItem.itemId, this.heldItem.count);
      }
      this.heldItem = null;
    }
    this.isOpen = false;
    this.overlay.style.display = 'none';
    this.cursorEl.style.display = 'none';
    document.removeEventListener('mousemove', this._onMouseMove);
    if (this._onClose) this._onClose(this.furnaceData);
  }

  updateProgress(progress, fuelLeft) {
    if (!this.furnaceData) return;
    this.furnaceData.progress = progress;
    this.furnaceData.fuelLeft = fuelLeft;
    // Update arrow fill
    this.arrowFill.style.width = (progress * 100) + '%';
    // Update flame fill (shrinks from top as fuel burns)
    this.flameFill.style.height = (fuelLeft * 100) + '%';
  }

  // --- Furnace slot interactions ---

  _getFurnaceSlot(name) {
    return this.furnaceData[name];
  }

  _setFurnaceSlot(name, item) {
    this.furnaceData[name] = item;
  }

  _onFurnaceSlotClick(name) {
    const current = this._getFurnaceSlot(name);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this._setFurnaceSlot(name, { ...this.heldItem });
        this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem)) {
        const space = MAX_STACK_SIZE - current.count;
        const transfer = Math.min(space, this.heldItem.count);
        current.count += transfer;
        this.heldItem.count -= transfer;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else {
        const tmp = current;
        this._setFurnaceSlot(name, { ...this.heldItem });
        this.heldItem = tmp;
      }
    } else if (!this._isEmpty(current)) {
      this.heldItem = { ...current };
      this._setFurnaceSlot(name, null);
    }
    this._render();
  }

  _onFurnaceSlotRightClick(name) {
    const current = this._getFurnaceSlot(name);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this._setFurnaceSlot(name, { ...this.heldItem, count: 1 });
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem) && current.count < MAX_STACK_SIZE) {
        current.count++;
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      }
    } else if (!this._isEmpty(current)) {
      const half = Math.ceil(current.count / 2);
      this.heldItem = { ...current, count: half };
      current.count -= half;
      if (current.count <= 0) this._setFurnaceSlot(name, null);
    }
    this._render();
  }

  // Output slot: only pick up, no placing items into it
  _onOutputSlotClick() {
    const output = this.furnaceData.output;
    if (this.heldItem) {
      // Can only pick up more of the same into held
      if (this._itemsMatch(this.heldItem, output) && !this._isEmpty(output)) {
        const space = MAX_STACK_SIZE - this.heldItem.count;
        const transfer = Math.min(space, output.count);
        this.heldItem.count += transfer;
        output.count -= transfer;
        if (output.count <= 0) this.furnaceData.output = null;
      }
      // Otherwise do nothing - can't place items in output
    } else if (!this._isEmpty(output)) {
      this.heldItem = { ...output };
      this.furnaceData.output = null;
    }
    this._render();
  }

  _onOutputSlotRightClick() {
    const output = this.furnaceData.output;
    if (!this.heldItem && !this._isEmpty(output)) {
      // Pick up half
      const half = Math.ceil(output.count / 2);
      this.heldItem = { ...output, count: half };
      output.count -= half;
      if (output.count <= 0) this.furnaceData.output = null;
    } else if (this.heldItem && this._itemsMatch(this.heldItem, output) && !this._isEmpty(output)) {
      // Pick up one more
      if (this.heldItem.count < MAX_STACK_SIZE) {
        this.heldItem.count++;
        output.count--;
        if (output.count <= 0) this.furnaceData.output = null;
      }
    }
    this._render();
  }

  // --- Player slot interactions (identical to ChestScreen) ---

  _onPlayerSlotClick(idx) {
    const current = this.inventory.getUnifiedSlot(idx);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem });
        this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem)) {
        const space = MAX_STACK_SIZE - current.count;
        const transfer = Math.min(space, this.heldItem.count);
        current.count += transfer;
        this.heldItem.count -= transfer;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else {
        const tmp = { ...current };
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem });
        this.heldItem = tmp;
      }
    } else if (!this._isEmpty(current)) {
      this.heldItem = { ...current };
      this.inventory.setUnifiedSlot(idx, null);
    }
    this._render();
  }

  _onPlayerSlotRightClick(idx) {
    const current = this.inventory.getUnifiedSlot(idx);
    if (this.heldItem) {
      if (this._isEmpty(current)) {
        this.inventory.setUnifiedSlot(idx, { ...this.heldItem, count: 1 });
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      } else if (this._itemsMatch(current, this.heldItem) && current.count < MAX_STACK_SIZE) {
        current.count++;
        this.heldItem.count--;
        if (this.heldItem.count <= 0) this.heldItem = null;
      }
    } else if (!this._isEmpty(current)) {
      const half = Math.ceil(current.count / 2);
      this.heldItem = { ...current, count: half };
      current.count -= half;
      if (current.count <= 0) this.inventory.setUnifiedSlot(idx, null);
    }
    this._render();
  }

  // --- Rendering ---

  _render() {
    // Render furnace slots
    this._renderSlot(this.inputSlot, this.furnaceData.input);
    this._renderSlot(this.fuelSlot, this.furnaceData.fuel);
    this._renderSlot(this.outputSlot, this.furnaceData.output);

    // Update progress arrow
    this.arrowFill.style.width = (this.furnaceData.progress * 100) + '%';

    // Update flame indicator
    this.flameFill.style.height = (this.furnaceData.fuelLeft * 100) + '%';

    // Render player storage (unified indices 9-35)
    for (let i = 0; i < 27; i++) {
      this._renderSlot(this.playerSlots[i], this.inventory.getUnifiedSlot(i + 9));
    }
    // Render hotbar (unified indices 0-8)
    for (let i = 0; i < 9; i++) {
      this._renderSlot(this.hotbarSlots[i], this.inventory.getUnifiedSlot(i));
    }
    // Render held item on cursor
    if (this.heldItem) {
      this.cursorEl.style.display = 'block';
      this.cursorEl.innerHTML = '';
      this._renderSlot(this.cursorEl, this.heldItem);
      this.cursorEl.style.background = 'transparent';
      this.cursorEl.style.border = 'none';
    } else {
      this.cursorEl.style.display = 'none';
    }
  }

  _renderSlot(slotEl, item) {
    slotEl.innerHTML = '';
    if (this._isEmpty(item)) return;

    // Render icon
    if (item.itemId !== undefined) {
      slotEl.innerHTML = renderItemIcon(item.itemId, 32);
    } else {
      slotEl.innerHTML = renderBlockIcon(item.blockId, 32);
    }

    if (item.count > 1) {
      const countEl = document.createElement('div');
      countEl.textContent = item.count;
      countEl.style.cssText = `
        position: absolute; bottom: 1px; right: 3px;
        font-size: 11px; font-weight: bold; color: white;
        text-shadow: 1px 1px 0 #000;
        pointer-events: none;
      `;
      slotEl.appendChild(countEl);
    }
  }
}
